<script context="module">
    import { get } from "svelte/store";
    import { dailyPlannerHourLabelWidth } from "../../../stores";
    import {
        accountCalendarEventType,
        CalendarEventCategoryTypes,
    } from "../../../Utils/utilEnums.svelte";

    import {
        getAccountCalendarEventType,
        getContrastBlackOrWhiteAgainstEventColor,
        getEventColor,
        getTime,
        getTitle,
        isObjectEmpty,
    } from "../../../Utils/utilFunctions.svelte";
    import { DailyPlannerSingleEventMaxHeightPx } from "./CalendarConstants.svelte";
    import { _checkEncounterOverlapEvents } from "./calendarHelper.svelte";

    const BASE_HEIGHT = 45,
        EVENTS_GAP = 15;

    export const calculatePosition = (time) => {
        if (!time) return;

        const [hours, minutes] = time.split(":").map(Number);

        return ((hours * 60 + minutes) / (24 * 60)) * 100;
    };

    export const calculateWidth = (start, end) => {
        if (!start || !end) return 0;

        const startPosition = calculatePosition(start);
        const endPosition = calculatePosition(end);
        return endPosition - startPosition;
    };

    export const formatTime = (hour) => {
        const period = hour < 12 ? "AM" : "PM";
        const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
        return `${formattedHour} ${period}`;
    };

    export const getFormatHHMM = (time) => {
        const [hour, min] = time.split(":");
        return `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
    };

    export const calculateTimeFromPosition = (positionPercent) => {
        if (!positionPercent) positionPercent = 0;

        const totalMinutes = (positionPercent / 100) * 24 * 60;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.floor(totalMinutes % 60);
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };

    export const calculateNewTimeByTimeBlocks = (
        timeBlocks,
        TIME_BLOCKS_PER_HOUR = 4,
        TIME_BLOCK = 15,
    ) => {
        const hours = Math.floor(timeBlocks / TIME_BLOCKS_PER_HOUR);
        const minutes = (timeBlocks % TIME_BLOCKS_PER_HOUR) * TIME_BLOCK;
        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    };

    export const getTimeBlocksByTime = (time, TIME_BLOCK = 15) => {
        const totalMins =
            Number(time.split(":")[0]) * 60 + Number(time.split(":")[1]);

        return Math.round(totalMins / TIME_BLOCK);
    };

    export const handleResize = (
        event,
        doctorIndex,
        resizingEvent,
        isResizingStart,
        TIME_BLOCKS_PER_HOUR = 4,
    ) => {
        if (!resizingEvent.hasOwnProperty("eventType")) {
            resizingEvent.newDoctorIndex = doctorIndex;

            resizingEvent.hasDoctorReassigned =
                resizingEvent.newDoctorIndex != resizingEvent.doctorIndex;
        }

        const target = event.currentTarget;
        const { left, width } = target.getBoundingClientRect();
        const mouseX = event.clientX - left;

        const timeBlocks = isResizingStart
            ? Math.round((mouseX / width) * TIME_BLOCKS_PER_HOUR * 24)
            : Math.ceil((mouseX / width) * TIME_BLOCKS_PER_HOUR * 24);

        let newTime = calculateNewTimeByTimeBlocks(timeBlocks);

        resizingEvent.start = isResizingStart
            ? newTime
            : resizingEvent.originalStart;
        resizingEvent.end = isResizingStart
            ? resizingEvent.originalEnd
            : newTime;

        const startPercent = calculatePosition(
            isResizingStart ? newTime : resizingEvent.originalStart,
        );
        const endPercent = calculatePosition(
            isResizingStart ? resizingEvent.originalEnd : newTime,
        );
        const newWidth = endPercent - startPercent;

        resizingEvent.hideDisplayText =
            _parseTimeToMin(resizingEvent.end) -
                _parseTimeToMin(resizingEvent.start) <=
            30;

        const dragPreviewData = {
            left: `calc(${calculatePosition(isResizingStart ? newTime : resizingEvent.originalStart)}% - 5px)`,
            width: `${newWidth}%`,
            doctorIndex: resizingEvent.newDoctorIndex ?? resizingEvent.doctorIndex,
        };

        return { resizingEvent, dragPreviewData };
    };

    export const handleSelectArea = (
        event,
        doctorIndex,
        selectionData,
        TIME_BLOCKS_PER_HOUR = 4,
    ) => {
        const boundingRect = event.currentTarget.getBoundingClientRect();
        const startXPercent =
            ((Math.min(
                selectionData.selectionStart,
                selectionData.selectionEnd,
            ) -
                boundingRect.left) /
                boundingRect.width) *
            100;
        const endXPercent =
            ((Math.max(
                selectionData.selectionStart,
                selectionData.selectionEnd,
            ) -
                boundingRect.left) /
                boundingRect.width) *
            100;

        const totalMinsStart = (startXPercent / 100) * 24 * 60;
        const totalMinsEnd = (endXPercent / 100) * 24 * 60;

        const newStart = calculateNewTimeByTimeBlocks(
            Math.round(totalMinsStart / (60 / TIME_BLOCKS_PER_HOUR)),
        );
        const newEnd = calculateNewTimeByTimeBlocks(
            Math.round(totalMinsEnd / (60 / TIME_BLOCKS_PER_HOUR)),
        );

        const startPercent = calculatePosition(newStart);
        const endPercent = calculatePosition(newEnd);

        // const left = `calc(${Math.min(startPercent, endPercent)}% - 5px)`;
        const left = Math.min(startPercent, endPercent);
        const width = Math.abs(endPercent - startPercent);

        return { ...selectionData, left, width, doctorIndex };
    };

    export const getTooltipPatientName = (patient) => {
        const { title, firstName, lastName } = patient;

        return `${title == 20 ? "" : `${getTitle(title)}.`} ${firstName} ${lastName}`;
    };

    export const mapAndMergeEncountersAndEvents = (
        doctor,
        encounters,
        events,
        showIndividualEncounters,
    ) => {
        const TIME_BLOCKS_PER_HOUR = 4;

        const doctorEncounters = [];

        if (encounters && encounters.length) {
            encounters.map((encounter) => {
                if (encounter.doctorId == doctor.id) {
                    const {
                        scheduledDate,
                        dischargeDate,
                        procedureName,
                        hospital,
                        surgeon,
                    } = encounter;

                    // all Encounters can only show in this color as it can't fall into any event category
                    encounter.eventColour = getEventColor(
                        CalendarEventCategoryTypes.PatientEncounter,
                    );

                    const encounterEndTime = _getEndTime(
                        getTime(scheduledDate),
                        getTime(dischargeDate),
                    );

                    doctorEncounters.push({
                        ...encounter,
                        start: getTime(scheduledDate),
                        end: encounterEndTime,
                        displayDescription: procedureName
                            ? procedureName
                            : `${encounterEndTime}`,
                        displayHospital: hospital ? hospital.name : "",
                        displaySurgeon: surgeon
                            ? `${getTitle(surgeon.title)} ${surgeon.firstName} ${surgeon.lastName}`
                            : "",
                        hideDisplayText:
                            _parseTimeToMin(encounterEndTime) -
                                _parseTimeToMin(getTime(scheduledDate)) <=
                            30,
                    });
                }
            });
        }

        const returned = mapEventsDateWithEncounters(
            doctor,
            events,
            doctorEncounters,
        );

        doctor = {
            ...returned.doctor,
            listedEncounter: showIndividualEncounters
                ? _splitEvent(returned.encounters)
                : [],
        };

        doctor.listedEncounter.map((encounterRow, i) => {
            doctor.encounterRowHeight[i] = BASE_HEIGHT;

            encounterRow.map((encounter) => {
                const html = `<div><p>${encounter.displayHospital}</p><p>${encounter.displaySurgeon}</p><p>${encounter.start} - ${encounter.end}</p><p>${encounter.procedureName}</p></div>`;

                const totalTimeBlocks =
                    getTimeBlocksByTime(
                        _adjustTimeInQuarterMins(encounter.end, false),
                    ) -
                    getTimeBlocksByTime(
                        _adjustTimeInQuarterMins(encounter.start),
                    );

                const containerWidth =
                    Math.ceil(totalTimeBlocks / TIME_BLOCKS_PER_HOUR) * get(dailyPlannerHourLabelWidth);

                const lineHeight = _getEventLineHight(
                    html,
                    containerWidth,
                    "0.8em",
                );

                doctor.encounterRowHeight[i] = Math.max(
                    lineHeight,
                    doctor.encounterRowHeight[i],
                );

                if (doctor.encounterRowHeight[i] > DailyPlannerSingleEventMaxHeightPx) {
                    if (doctor.listedEncounter.length >= 1 && i < doctor.listedEncounter.length)
                        doctor.encounterRowHeight[i] = DailyPlannerSingleEventMaxHeightPx + 5;
                    else 
                        doctor.encounterRowHeight[i] = DailyPlannerSingleEventMaxHeightPx;
                }
            });
        });

        doctor.encounterRowHeight.map(
            (rowHeight) => (doctor.lineHeight += rowHeight),
        );

        doctor.lineHeight = Math.max(
            BASE_HEIGHT,
            doctor.lineHeight
        );

        return doctor;
    };

    // export const mapEncountersData = (
    //     doctor,
    //     encounters = [],
    //     TIME_BLOCKS_PER_HOUR = 4,
    // ) => {
    //     if (!encounters) return doctor;

    //     const BASE_EMPTY_HEIGHT = 20;

    //     encounters.map((encounter) => {
    //         if (encounter.doctorId == doctor.id) {
    //             const {
    //                 scheduledDate,
    //                 dischargeDate,
    //                 procedureName,
    //                 hospital,
    //                 surgeon,
    //             } = encounter;

    //             // all Encounters can only show in this color as it can't fall into any event category
    //             encounter.eventColour = getEventColor(
    //                 CalendarEventCategoryTypes.PatientEncounter,
    //             );

    //             const encounterEndTime = _getEndTime(
    //                 getTime(scheduledDate),
    //                 getTime(dischargeDate),
    //             );

    //             doctor.encounters.push({
    //                 ...encounter,
    //                 start: getTime(scheduledDate),
    //                 end: encounterEndTime,
    //                 displayDescription: procedureName
    //                     ? procedureName
    //                     : `${encounterEndTime}`,
    //                 displayHospital: hospital ? hospital.name : "",
    //                 displaySurgeon: surgeon
    //                     ? `${getTitle(surgeon.title)} ${surgeon.firstName} ${surgeon.lastName}`
    //                     : "",
    //                 hideDisplayText:
    //                     _parseTimeToMin(encounterEndTime) -
    //                         _parseTimeToMin(getTime(scheduledDate)) <=
    //                     30,
    //             });
    //         }
    //     });

    //     doctor.listedEncounter = _splitEvent(doctor.encounters);

    //     if (!doctor.listedEncounter?.length)
    //         doctor.encounterRowHeight[0] = BASE_EMPTY_HEIGHT;
    //     else {
    //         doctor.listedEncounter.map((encounterRow, i) => {
    //             doctor.encounterRowHeight[i] = BASE_HEIGHT;

    //             encounterRow.map((encounter) => {
    //                 const html = `<div><p>${encounter.displayHospital}</p><p>${encounter.displaySurgeon}</p><p>${encounter.start} - ${encounter.end}</p><p>${encounter.procedureName}</p></div>`;

    //                 const totalTimeBlocks =
    //                     getTimeBlocksByTime(
    //                         _adjustTimeInQuarterMins(encounter.end, false),
    //                     ) -
    //                     getTimeBlocksByTime(
    //                         _adjustTimeInQuarterMins(encounter.start),
    //                     );

    //                 const containerWidth =
    //                     Math.ceil(totalTimeBlocks / TIME_BLOCKS_PER_HOUR) * get(dailyPlannerHourLabelWidth);

    //                 const lineHeight = _getEventLineHight(
    //                     html,
    //                     containerWidth,
    //                     "0.8em",
    //                 );

    //                 doctor.encounterRowHeight[i] = Math.max(
    //                     lineHeight,
    //                     doctor.encounterRowHeight[i],
    //                 );
    //             });
    //         });
    //     }

    //     doctor.encounterRowHeight.map(
    //         (rowHeight) => (doctor.lineHeight += rowHeight),
    //     );

    //     return doctor;
    // };

    export const mapEventsDateWithEncounters = (
        doctor,
        events = [],
        encounters = [],
    ) => {
        if (!events) return doctor;

        const TIME_BLOCKS_PER_HOUR = 4;

        // Order: Leave - On-call - List
        const eventOrderMap = {
            0: 0, // Leave
            1: 2, // List
            2: 1, // On-call
        };

        events
            .sort(
                (a, b) =>
                    accountCalendarEventType.find(
                        (ace) => ace.id == b.eventType,
                    )?.order -
                    accountCalendarEventType.find(
                        (ace) => ace.id == a.eventType,
                    )?.order,
            )
            .map((event) => {
                if (event.doctorId == doctor.id) {
                    const {
                        eventType,
                        description,
                        fromDate,
                        toDate,
                        eventCategoryName,
                        hospitalName,
                        surgeonName,
                    } = event;

                    const startTime = getTime(fromDate);
                    const endTime = _getEndTime(
                        getTime(fromDate),
                        getTime(toDate),
                    );

                    if (eventType === 1) event.nestedEncounter = [];

                    const addedEncounterIDs = [];

                    if (encounters.length && eventType === 1) {
                        encounters.forEach((ect) => {
                            const { scheduledDate, dischargeDate } = ect;
                            if (
                                _checkEncounterOverlapEvents(
                                    scheduledDate,
                                    dischargeDate,
                                    fromDate,
                                    toDate,
                                )
                            ) {
                                event.nestedEncounter.push(ect);
                                addedEncounterIDs.push(ect.id);
                            }
                        });
                    }

                    if (addedEncounterIDs.length)
                        encounters = encounters.filter(
                            (ect) => !addedEncounterIDs.includes(ect.id),
                        );

                    const displayDescription = `${getAccountCalendarEventType(
                        eventType,
                    )}${eventCategoryName ? " - Category " + eventCategoryName + "," : ""}${
                        hospitalName ? " " + hospitalName : " "
                    }${
                        surgeonName ? " with " + surgeonName : ""
                    } , ${startTime} - ${endTime}${
                        description ? `, Description ${description}` : ""
                    }${
                        eventType === 1 && event?.nestedEncounter?.length
                            ? " Encounters: " + event.nestedEncounter.length
                            : ""
                    }`;

                    const displayCategory = `${eventCategoryName ? `${eventCategoryName}` : `${getAccountCalendarEventType(eventType)}`}`;
                    const displayHospital = `${hospitalName ? hospitalName : ""}`;
                    const displaySurgeon = `${surgeonName ? surgeonName : ""}`;
                    const displayTime = `${startTime} - ${endTime} ${
                        eventType === 1 && event?.nestedEncounter?.length
                            ? " Encounters: " + event.nestedEncounter.length
                            : ""
                    }`;

                    const html = `<p>${displayCategory}</p>${displayHospital ? `<p>${displayHospital}</p>` : ""}${displaySurgeon ? `<p>${displaySurgeon}</p>` : ""}<p>${displayTime}</p>`;

                    const totalTimeBlocks =
                        getTimeBlocksByTime(
                            _adjustTimeInQuarterMins(endTime, false),
                        ) -
                        getTimeBlocksByTime(
                            _adjustTimeInQuarterMins(startTime),
                        );

                    const lineHeight =
                        _getEventLineHight(
                            html,
                            Math.ceil(totalTimeBlocks / TIME_BLOCKS_PER_HOUR) *
                                get(dailyPlannerHourLabelWidth),
                            "0.8em",
                        ) + EVENTS_GAP;

                    event = {
                        ...event,
                        start: startTime,
                        end: endTime,
                        displayDescription,
                        displayCategory,
                        displayHospital,
                        displaySurgeon,
                        displayTime,
                        eventColour: getEventColor(event),
                        fontColor: getContrastBlackOrWhiteAgainstEventColor(
                            event,
                            "#FFFFFF",
                            "#000000",
                        ),
                        totalTimeBlocks,
                        lineHeight,
                        hideDisplayText:
                            _parseTimeToMin(endTime) -
                                _parseTimeToMin(startTime) <=
                            30,
                        isRepeating:
                            (event.eventType == 1 || event.eventType == 2) &&
                            event.repeatIntervalType > 0,
                    };

                    const eventOrder = eventOrderMap[eventType];

                    if (!doctor.eventsMap[eventOrder])
                        doctor.eventsMap[eventOrder] = {};

                    if (!doctor.eventsMap[eventOrder].listedEvent)
                        doctor.eventsMap[eventOrder].listedEvent = [];

                    doctor.eventsMap[eventOrder].listedEvent.push({
                        ...event,
                        eventOrder,
                    });
                }
            });

        // check and handle overlapping events
        let cumulativeHeights = {}; // save total height of each eventType

        for (let key in doctor.eventsMap) {
            cumulativeHeights[key] = 0;

            doctor.eventsMap[key].listedEvent = _splitEvent(
                doctor.eventsMap[key].listedEvent,
            );

            doctor.eventsMap[key].eventRowHeight = [];

            const listedEvent = doctor.eventsMap[key].listedEvent;

            listedEvent.map((eventRow, i) => {
                doctor.eventsMap[key].eventRowHeight[i] = 0;

                eventRow.map((event) => {
                    // calculate relative height of event
                    let relativeHeight = cumulativeHeights[key];
                    for (let otherKey in cumulativeHeights) {
                        if (otherKey !== key) {
                            relativeHeight += cumulativeHeights[otherKey];
                        }
                    }

                    event.relativeHeight = relativeHeight;

                    doctor.eventsMap[key].eventRowHeight[i] = Math.max(
                        event.lineHeight,
                        doctor.eventsMap[key].eventRowHeight[i],
                    );
                });

                cumulativeHeights[key] +=
                    doctor.eventsMap[key].eventRowHeight[i];

                doctor.lineHeight += doctor.eventsMap[key].eventRowHeight[i];
            });
        }

        return { doctor, encounters };
    };

    // HELPER FUNCTIONS
    const _parseTimeToMin = (time) => {
        const [hours, mins] = time.split(":").map(Number);
        return hours * 60 + mins;
    };

    const _getEndTime = (start, end) => {
        const __addMinutes__ = (time, minutes) => {
            if (!time) return;

            const [hours, mins] = time.split(":").map(Number);
            const totalMinutes = hours * 60 + mins + minutes;
            const newHours = Math.floor(totalMinutes / 60);
            const newMinutes = Math.floor(totalMinutes % 60);
            return `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}`;
        };

        if (_parseTimeToMin(end) - _parseTimeToMin(start) >= 15) return end;

        return __addMinutes__(start, 15);
    };

    const _getEventLineHight = (html, containerWidth, fontSize) => {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.visibility = "hidden";
        container.style.height = "auto";
        container.style.width = `${containerWidth}px`;
        container.style.fontSize = `${fontSize}`;
        container.style.whiteSpace = "pre-wrap";
        container.style.top = "-9999px";
        container.style.padding = "2px";
        container.style.borderRadius = "4px";
        container.style.border = "1px solid #fff";
        container.style.textAlign = "center";
        container.style.display = "flex";
        container.style.flexDirection = "column";

        const formattedHtml = html.replace(/\n/g, " ");

        container.innerHTML = formattedHtml;

        document.body.appendChild(container);

        const containerHeight = container.offsetHeight;

        document.body.removeChild(container);

        return containerHeight;
    };

    const _adjustTimeInQuarterMins = (timeStr, isStartTime = true) => {
        let [hours, minutes] = timeStr.split(":").map(Number);

        let totalMinutes = hours * 60 + minutes;

        totalMinutes = isStartTime
            ? Math.ceil(totalMinutes / 15) * 15
            : Math.floor(totalMinutes / 15) * 15;

        let newHours = Math.floor(totalMinutes / 60);
        let newMinutes = totalMinutes % 60;

        newHours = newHours.toString().padStart(2, "0");
        newMinutes = newMinutes.toString().padStart(2, "0");

        return `${newHours}:${newMinutes}`;
    };

    const _splitEvent = (events = []) => {
        let listedEvents = [];

        if (!events.length) return listedEvents;

        if (!listedEvents.length) listedEvents.push([events[0]]);

        if (events.length == 1) return listedEvents;

        const __isOverlap__ = (evt1, evt2) =>
            !(
                new Date(evt1.toDate) <= new Date(evt2.fromDate) ||
                new Date(evt1.fromDate) >= new Date(evt2.toDate)
            );

        for (let i = 1; i < events.length; i++) {
            const listCanAddEvent = listedEvents.some(
                (list) => !list.some((evt) => __isOverlap__(evt, events[i])),
            );

            if (!listCanAddEvent) listedEvents.push([events[i]]);
            else {
                const targetListIndex = listedEvents.findIndex(
                    (list) =>
                        !list.some((evt) => __isOverlap__(evt, events[i])),
                );

                listedEvents[targetListIndex].push(events[i]);
            }
        }

        return listedEvents;
    };

    export const getShortDoctorName = (doctor) => {
        if (!doctor || isObjectEmpty(doctor)) return "-";
        let returnedName = "";
        returnedName += doctor.titleName ?? "";
        if (doctor.firstName)
            returnedName += ` ${doctor.firstName.charAt(0).toUpperCase()}`;
        if (doctor.middleName)
            returnedName += ` ${doctor.middleName.charAt(0).toUpperCase()}`;
        if (doctor.lastName)
            returnedName += ` ${doctor.lastName.charAt(0).toUpperCase()}`;
        return returnedName;
    };
</script>
